/**
 * The name box and the formula bar (Requirement 1.9).
 *
 * "WHEN a user selects a Cell, THE Grid_Renderer SHALL display the Cell_Reference
 * of that Cell in the name box and the Cell content in the formula bar." Two
 * one-line displays, and two inputs — because both are also the way a user *acts*:
 * typing `C400` in the name box goes there, and typing in the formula bar edits the
 * active cell.
 *
 * The component owns no workbook state. It is handed a {@link CellContent} — the
 * kernel's own answer for the active cell, formula source included (see
 * `src/kernel/view.ts`) — and it renders it. That is what keeps the bar honest: the
 * formula bar shows `=A1*2`, not `42`, because the kernel says the cell's input is
 * `=A1*2`, and nothing here has to know the difference between a literal and a
 * formula.
 *
 * Editing rule, kept small on purpose: the bar is a *display* until the user
 * focuses it, at which point it stops being overwritten by selection updates until
 * the edit is committed with Enter or abandoned with Escape or a blur. Without that
 * rule, an incoming `changed` broadcast would yank half-typed text out from under
 * the caret. The in-cell editor, `Alt+Enter`, autocomplete, and reference
 * highlighting are task 12's; this is the bar, and it commits through the same
 * `setCell` command the editor will.
 */

import { addrToA1, colToLabel, parseA1Range, rangeToA1 } from '../core/a1.js';
import type { CellContent } from '../grid/viewTile.js';
import { MAX_COLS, MAX_ROWS, normalizeRange, type Addr, type Range } from '../core/types.js';
import { el } from './dom.js';

export interface FormulaBarCallbacks {
  /** A committed name-box entry, already resolved to a range on the active sheet. */
  onNavigate(range: Range): void;
  /** A rejected name-box entry, so the shell can say why. */
  onNavigateError(text: string): void;
  /** A committed formula-bar entry: typed text for the active cell. */
  onCommit(text: string): void;
}

export interface FormulaBar {
  element: HTMLElement;
  /** The name box input (Requirement 1.9). */
  nameBox: HTMLInputElement;
  /** The formula bar input (Requirement 1.9). */
  input: HTMLInputElement;
  /** Points the bar at a new selection. */
  setSelection(active: Addr, selection: Range): void;
  /** Shows the active cell's content. `null` clears the bar. */
  setContent(content: CellContent | null): void;
  /** True while the user is typing in the formula bar. */
  editing(): boolean;
}

/**
 * The reference the name box shows.
 *
 * A single cell shows its own address; a rectangle shows the whole range, which is
 * still a Cell_Reference in the glossary's sense (`C2:D400`) and is what a user
 * reaching for the name box after a drag expects to see.
 */
export function nameBoxText(active: Addr, selection: Range): string {
  const r = normalizeRange(selection);
  if (r.r0 === 0 && r.r1 === MAX_ROWS - 1 && r.c0 === 0 && r.c1 === MAX_COLS - 1) {
    return 'A:ZZZ';
  }
  if (r.r0 === 0 && r.r1 === MAX_ROWS - 1) {
    const col0 = colToLabel(r.c0);
    const col1 = colToLabel(r.c1);
    return r.c0 === r.c1 ? `${col0}:${col0}` : `${col0}:${col1}`;
  }
  if (r.c0 === 0 && r.c1 === MAX_COLS - 1) {
    const row0 = r.r0 + 1;
    const row1 = r.r1 + 1;
    return r.r0 === r.r1 ? `${row0}:${row0}` : `${row0}:${row1}`;
  }
  const single = r.r0 === r.r1 && r.c0 === r.c1;
  return single ? addrToA1(active) : rangeToA1(r);
}

export function createFormulaBar(callbacks: FormulaBarCallbacks): FormulaBar {
  const nameBox = el('input', {
    className: 'name-box',
    testid: 'name-box',
    ariaLabel: 'Name box',
    attrs: { type: 'text', autocomplete: 'off', spellcheck: 'false' },
  });

  const input = el('textarea', {
    className: 'formula-input',
    testid: 'formula-bar',
    ariaLabel: 'Formula bar',
    attrs: { rows: '1', autocomplete: 'off', spellcheck: 'false' },
  }) as unknown as HTMLInputElement;

  const expandBtn = el('button', {
    className: 'formula-expand-btn',
    testid: 'formula-expand-btn',
    ariaLabel: 'Expand formula bar',
    title: 'Expand formula bar',
    text: '˅',
  });

  let expanded = false;
  expandBtn.addEventListener('click', () => {
    expanded = !expanded;
    element.classList.toggle('is-expanded', expanded);
    expandBtn.textContent = expanded ? '˄' : '˅';
    expandBtn.title = expanded ? 'Collapse formula bar' : 'Expand formula bar';
  });

  const element = el(
    'div',
    { className: 'formula-bar', testid: 'formula-bar-row', role: 'group', ariaLabel: 'Cell reference and content' },
    [
      nameBox,
      el('span', { className: 'formula-fx', text: 'fx', attrs: { 'aria-hidden': 'true' } }),
      input as unknown as HTMLElement,
      expandBtn,
    ],
  );

  let active: Addr | null = null;
  let currentSelection: Range | null = null;
  let last = '';
  let focused = false;

  nameBox.addEventListener('focus', () => {
    nameBox.select();
  });

  nameBox.addEventListener('blur', () => {
    if (active !== null && currentSelection !== null) {
      nameBox.value = nameBoxText(active, currentSelection);
    }
  });

  nameBox.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const sheet = active?.sheet;
      const parsed = sheet === undefined ? null : parseA1Range(nameBox.value.trim());
      if (parsed === null || sheet === undefined) {
        callbacks.onNavigateError(nameBox.value.trim());
        if (active !== null && currentSelection !== null) {
          nameBox.value = nameBoxText(active, currentSelection);
        }
        return;
      }
      nameBox.blur();
      callbacks.onNavigate(
        normalizeRange({
          sheet,
          r0: parsed.from.row,
          c0: parsed.from.col,
          r1: parsed.to.row,
          c1: parsed.to.col,
        }),
      );
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (active !== null && currentSelection !== null) {
        nameBox.value = nameBoxText(active, currentSelection);
      }
      nameBox.blur();
    }
  });

  input.addEventListener('focus', () => {
    focused = true;
  });

  input.addEventListener('blur', () => {
    focused = false;
    input.value = last;
  });

  input.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      focused = false;
      callbacks.onCommit(input.value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      focused = false;
      input.value = last;
      input.blur();
    }
  });

  return {
    element,
    nameBox,
    input,

    setSelection(next: Addr, selection: Range): void {
      active = next;
      currentSelection = selection;
      // Never fight the caret: a user mid-entry in the name box keeps their text.
      if (document.activeElement !== nameBox) nameBox.value = nameBoxText(next, selection);
    },

    setContent(content: CellContent | null): void {
      last = content === null ? '' : content.input;
      if (!focused) input.value = last;
    },

    editing(): boolean {
      return focused;
    },
  };
}
